import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import {
  RESTAURANT,
  HOURS as DEFAULT_HOURS,
  MENU as DEFAULT_MENU,
  REVIEWS as DEFAULT_REVIEWS,
} from "@/lib/content";

export const DEFAULT_CONTENT = {
  hero: {
    chip: "Manchester · Chinatown",
    eyebrow: "Est. Authentic Thai",
    headline_line1: "Real Thai",
    headline_line2_italic: "flavours,",
    headline_line3_red: "wok-fired",
    headline_line4: "daily.",
    subhead:
      "A tiny, bustling kitchen tucked above George Street — serving Bangkok street classics with ingredients flown in weekly.",
    tagline_th: "รสชาติคนไทย 100%.",
    cta_primary_label: "View the menu",
    price_tag: "From £12.99",
    today_caption: "Tonight's wok is lit.",
    hours_chip_number: "12—22",
    hours_chip_sub: "Tue–Thu open late",
  },
  about: {
    chip: "Our story",
    title_line1: "Experience",
    title_line2_italic: "real",
    title_line2_rest: "Thai flavours",
    title_line3: "in the heart of",
    title_line3_red: "Chinatown",
    paragraphs:
      "Our mission is simple: bring the authentic taste of Thailand to Manchester. A cozy dining room, a handful of tables, and a kitchen led by a chef who grew up selling noodles on the banks of the Chao Phraya.\n\nWith a stubborn commitment to quality ingredients and genuine flavours, we aim to be your go-to spot for real Thai cuisine — whether you're popping in for a quick bowl of Tom Yum or bringing the whole family for a feast.",
    pillars: [
      { icon: "heart", title: "Family recipes", text: "Handed down and tuned for decades — nothing from a jar." },
      { icon: "leaf", title: "Weekly air-freight", text: "Thai basil, galangal and kaffir lime, flown in from Bangkok markets." },
      { icon: "flame", title: "Real wok heat", text: "Cooked at 300°C for that unmistakable smoky 'wok hei'." },
      { icon: "users", title: "Thai-run kitchen", text: "Every dish personally checked by our head chef from Ayutthaya." },
    ],
  },
  hours: DEFAULT_HOURS.map((h) => ({
    day: h.day,
    time: h.time,
    closed: !!h.closed,
  })),
  contact: {
    phone: RESTAURANT.phone,
    email: RESTAURANT.email,
    address_line1: RESTAURANT.address.line1,
    address_line2: RESTAURANT.address.line2,
    postcode: RESTAURANT.address.postcode,
    country: RESTAURANT.address.country,
    maps_embed: RESTAURANT.mapsEmbed,
  },
  menu: DEFAULT_MENU.map((m) => ({
    id: m.id,
    name: m.name,
    th: m.th || "",
    desc: m.desc,
    price: m.price,
    tag: m.tag || "",
    image: m.image || "",
  })),
  reviews: DEFAULT_REVIEWS.map((r) => ({
    id: r.name,
    name: r.name,
    meta: r.meta,
    when: r.when,
    text: r.text,
    rating: r.rating || 5,
  })),
  full_menu_image:
    "https://customer-assets.emergentagent.com/job_authentic-thai-taste/artifacts/2e4t88xw_IMG_3337.jpeg",
};

const SiteContentCtx = createContext({
  content: DEFAULT_CONTENT,
  loading: true,
  refresh: () => {},
});

function mergeContent(remote) {
  if (!remote || typeof remote !== "object") return DEFAULT_CONTENT;
  const out = { ...DEFAULT_CONTENT };
  for (const key of Object.keys(DEFAULT_CONTENT)) {
    const v = remote[key];
    if (Array.isArray(v) && v.length) out[key] = v;
    else if (v && !Array.isArray(v) && typeof v === "object") {
      out[key] = { ...DEFAULT_CONTENT[key], ...v };
    }
  }
  return out;
}

export function SiteContentProvider({ children }) {
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/content");
      setContent(mergeContent(data));
    } catch (e) {
      setContent(DEFAULT_CONTENT);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const value = useMemo(() => ({ content, loading, refresh: load }), [content, loading]);
  return <SiteContentCtx.Provider value={value}>{children}</SiteContentCtx.Provider>;
}

export function useSiteContent() {
  return useContext(SiteContentCtx);
}
